import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - functions
import { WEB_SERVICE_MIMS, protocols } from "../../config/constants";
import { filterDataByDate, getTrainedMinutesAndSeconds } from "../../config/functions";
import { isClinicOrHome, isAppOrPlus } from "../../config/validateApplication";
import { isAISNProjectHospital } from "../../config/projectFunctions";
import {
  isOnlyProtocolWithScore,
  isProtocolWithoutDifficultyModulators,
  isProtocolWithoutHitsAndErrors,
  validateApplicationProtocol,
  isAppOrPlusProtocol,
  getProtocolInfoPrefix,
} from "../../config/validateProtocols";
import { GetClinicAndHomeProtocolData } from "./sections/getProtocolData/clinicAndHome";
import { GetAppBasicsProtocolData } from "./sections/getProtocolData/appBasics";
import { GetAppRotationProtocolData } from "./sections/getProtocolData/appRotation";
import { GetAppARProtocolData } from "./sections/getProtocolData/appAR";
import { GetWebHandTrackingProtocolData } from "./sections/getProtocolData/webHandTracking";
import { GetWebFullBodyProtocolData } from "./sections/getProtocolData/webFullBody";
// Components
import {
  CurrentResponsiveImg,
  InfoButton,
  InfoText,
  InfoArrowImg,
  IndicatorMainDiv,
  IndicatorDiv,
  ProtocolVideoButton,
  ProtocolVideoButtonResponsive,
  ButtonAndImgDiv,
  VideoButtonImg,
  VideoText,
  CurrentImg,
  IndicatorTextDiv,
  IndicatorText,
  IndicatorTitle,
  IndicatorDataDiv,
  IndicatorDataText,
  SessionHeaderMainDiv,
  SessionHeaderDiv,
  SessionTitle,
  SessionHideDiv,
} from "./styles";
import Loader from "../loader";
import Axios from "../../config/axios";
import PatientNavigation from "../patientNavigation";
import AdherenceContainer from "./sections/adherenceContainer";
import Title from "../title";
import SessionData from "./sections/sessionData";
import ProtocolNavigation from "./sections/protocolNavigation";
import ProtocolVideoPopup from "../protocolVideoPopup";
import ScorePlot from "./sections/scorePlot";
import DifficultyModulatorsPlot from "./sections/difficultyModulatorsPlot";
import HitsAndErrorsPlot from "./sections/hitsAndErrorsPlot";
import SessionDurationPlot from "./sections/sessionDurationPlot";
import DistanceCoveredPlot from "./sections/distanceCoveredPlot";
import AreaCoveredPlot from "./sections/areaCoveredPlot";
import LeftElbowPlot from "./sections/leftElbowPlot";
import RightElbowPlot from "./sections/rightElbowPlot";
import TotalDisplacementPlot from "./sections/totalDisplacementPlot";
import VolumePlot from "./sections/volumePlot";
import SurfaceAreaPlot from "./sections/surfaceAreaPlot";
import AreaXYZPlot from "./sections/areaXYZPlot";
// Assets
import ArrowDown from "../../assets/icons/ico_arrow_down_black.svg";
import ArrowUp from "../../assets/icons/ico_arrow_up_black.svg";
import IcoOpenVideo from "../../assets/icons/ico_video.svg";

const Reports = () => {
  const [isShowInfo, setIsShowInfo] = useState(false);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [sessionTimeRange, setSessionTimeRange] = useState("WEEK");
  const [toggleRechargeReports, setToggleRechargeReports] = useState(false);
  const [isProtocolVideoShowed, setIsProtocolVideoShowed] = useState(false);
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const { patientId, protocolId, software } = useParams();
  const [t] = useTranslation("global");
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const clinicianId = useSelector(({ clinician }) => clinician.data.CLINICIAN_ID);
  // Protocol info
  const [protocolTitle, setProtocolTitle] = useState("");
  const [protocolDescription, setProtocolDescription] = useState("");
  const [protocolImg, setProtocolImg] = useState("");
  const [protocolResponsiveImg, setProtocolResponsiveImg] = useState("");
  const [protocolVideo, setProtocolVideo] = useState("");
  // Recordings
  const [recordings, setRecordings] = useState([]);
  const [currentRecordings, setCurrentRecordings] = useState([]);
  // Difficulty modulators
  const [difficultyModulators, setDifficultyModulators] = useState([]);
  const [currentDifficultyModulators, setCurrentDifficultyModulators] = useState([]);
  // Set Metrics
  const [metrics, setMetrics] = useState([]);
  const [currentMetrics, setCurrentMetrics] = useState([]);
  // Set plot images
  const scorePlotRef = useRef(null);
  const difficultyModulatorsPlotRef = useRef(null);
  const hitsAndErrorsPlotRef = useRef(null);
  const sessionDurationPlotRef = useRef(null);
  const distanceCoveredPlotRef = useRef(null);
  const areaCoveredPlotRef = useRef(null);
  const leftElbowPlotRef = useRef(null);
  const rightElbowPlotRef = useRef(null);
  const totalDisplacementPlotRef = useRef(null);
  const volumePlotRef = useRef(null);
  const surfaceAreaPlotRef = useRef(null);
  const AreaXYZPlotRef = useRef(null);

  // Save clinician activity
  useEffect(() => {
    const clinicianActivityData = {
      CLINICIAN_ID: clinicianId,
      PATIENT_ID: patientId,
      PROTOCOL_ID: protocolId,
    };
    Axios("post", `${WEB_SERVICE_MIMS}/clinicians/protocol-activity`, clinicianActivityData).catch((error) => {
      console.log("Add clinician activity: ", error);
    });
  }, [clinicianId, patientId, protocolId]);

  // Fetch Recordings
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/reports/protocol/get/${patientId}/${protocolId}/${software}`)
      .then(({ data }) => {
        const fetchedRecordings = data.sort((a, b) => new Date(a.STARTING_DATE) - new Date(b.STARTING_DATE));
        setRecordings(fetchedRecordings);
        setIsLoadingReports(false);
      })
      .catch((error) => {
        console.log("Get protocol recordings: ", error);
      });

    return () => {
      setRecordings([]);
      setCurrentRecordings([]);
      setIsLoadingReports(true);
    };
  }, [patientId, protocolId, software, toggleRechargeReports]);

  // Update Recordings
  useEffect(() => {
    const currentRecordings = filterDataByDate(sessionTimeRange, recordings);
    setCurrentRecordings(currentRecordings);
  }, [recordings, sessionTimeRange]);

  // Fetch Difficulty Modulators
  useEffect(() => {
    if (isAppOrPlus(software) && !isProtocolWithoutDifficultyModulators(protocolId)) {
      Axios("get", `${WEB_SERVICE_MIMS}/difficulty-modulators/by-protocol/${protocolId}/${patientId}/${software}`)
        .then(({ data }) => {
          const fetchedDifficultyModulators = data.sort((a, b) => new Date(a.STARTING_DATE) - new Date(b.STARTING_DATE));
          setDifficultyModulators(fetchedDifficultyModulators);
        })
        .catch((error) => {
          console.log("Get Difficulty Modulators: ", error);
        });
    }

    return () => {
      setDifficultyModulators([]);
    };
  }, [protocolId, patientId, software, toggleRechargeReports]);

  // Update Difficulty Modulators
  useEffect(() => {
    if (isAppOrPlus(software) && !isProtocolWithoutDifficultyModulators(protocolId)) {
      const currentDifficultyModulators = filterDataByDate(sessionTimeRange, difficultyModulators);
      setCurrentDifficultyModulators(currentDifficultyModulators);
    }
  }, [sessionTimeRange, difficultyModulators, software]);

  // Fetch Metrics
  useEffect(() => {
    if (validateApplicationProtocol(protocols.app.ar, protocolId)) {
      Axios("get", `${WEB_SERVICE_MIMS}/metrics/protocol/get/${patientId}/${protocolId}/${software}`)
        .then(({ data }) => {
          const fetchedMetrics = data.sort((a, b) => new Date(a.STARTING_DATE) - new Date(b.STARTING_DATE));
          setMetrics(fetchedMetrics);
        })
        .catch((error) => {
          console.log("Get metrics: ", error);
        });
    }

    return () => {
      setMetrics([]);
    };
  }, [patientId, protocolId, software, toggleRechargeReports]);

  // Update Metrics
  useEffect(() => {
    if (validateApplicationProtocol(protocols.app.ar, protocolId)) {
      const currentMetrics = filterDataByDate(sessionTimeRange, metrics);
      setCurrentMetrics(currentMetrics);
    }
  }, [protocolId, sessionTimeRange, metrics]);

  // Set protocol info
  useEffect(() => {
    let protocolData = {};

    if (validateApplicationProtocol(protocols.clinicAndHome, protocolId)) protocolData = GetClinicAndHomeProtocolData(protocolId);

    if (validateApplicationProtocol(protocols.app.basic, protocolId)) protocolData = GetAppBasicsProtocolData(protocolId);
    if (validateApplicationProtocol(protocols.app.rotation, protocolId)) protocolData = GetAppRotationProtocolData(protocolId);
    if (validateApplicationProtocol(protocols.app.ar, protocolId)) protocolData = GetAppARProtocolData(protocolId);

    if (validateApplicationProtocol(protocols.web.handTracking, protocolId)) protocolData = GetWebHandTrackingProtocolData(protocolId);
    if (validateApplicationProtocol(protocols.web.fullBody, protocolId)) protocolData = GetWebFullBodyProtocolData(protocolId);

    const { img, imgResponsive, name, description, video } = protocolData;

    setProtocolTitle(t(name));
    setProtocolDescription(t(description));
    setProtocolImg(img);
    setProtocolResponsiveImg(imgResponsive);
    if (video) setProtocolVideo(t(video));
  }, [software, protocolId, t]);

  // Set AISN project flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isAISNHospital = await isAISNProjectHospital(hospitalName);
      setIsAISNHospital(isAISNHospital);
    };
    handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [hospitalName]);

  const handleShowProtocolVideo = () => setIsProtocolVideoShowed(true);

  const renderButtonExtraInfo = () => {
    if (isAppOrPlus(software) && protocolVideo) {
      return (
        <ProtocolVideoButtonResponsive onClick={handleShowProtocolVideo} responsiveDisplay>
          <CurrentResponsiveImg src={protocolResponsiveImg} alt="protocol-img" />
          <ButtonAndImgDiv>
            <VideoButtonImg src={IcoOpenVideo} alt="ico-video" />
            <VideoText>{t("reports_videoText")}</VideoText>
          </ButtonAndImgDiv>
        </ProtocolVideoButtonResponsive>
      );
    }
    return <CurrentResponsiveImg src={protocolResponsiveImg} alt="protocol-img" allBorder />;
  };

  const renderExtraProtocolInfo = () => {
    const handleShowInfo = () => setIsShowInfo(!isShowInfo);
    return (
      <>
        <InfoButton onClick={handleShowInfo}>
          <InfoText>{t("informationButton_title")}</InfoText>
          <InfoArrowImg src={isShowInfo ? ArrowUp : ArrowDown} alt="ico-arrow" />
        </InfoButton>
        <IndicatorMainDiv display={isShowInfo ? "flex" : "none"}>
          <IndicatorDiv>
            {isAppOrPlus(software) && protocolVideo ? (
              <ProtocolVideoButton onClick={handleShowProtocolVideo}>
                <CurrentImg src={protocolImg} alt="protocol-img" />
                <ButtonAndImgDiv>
                  <VideoButtonImg src={IcoOpenVideo} alt="ico-video" />
                  <VideoText>{t("reports_videoText")}</VideoText>
                </ButtonAndImgDiv>
              </ProtocolVideoButton>
            ) : (
              <CurrentImg src={protocolImg} alt="protocol-img" allBorder />
            )}
            <IndicatorTextDiv>
              <IndicatorText>
                <IndicatorTitle>{t("protocols_descriptionTitle")}: </IndicatorTitle>
                {protocolDescription}
              </IndicatorText>
            </IndicatorTextDiv>
          </IndicatorDiv>
        </IndicatorMainDiv>
      </>
    );
  };

  const renderProtocolVideoPopup = () => {
    if (isAppOrPlus(software) && isProtocolVideoShowed) {
      return <ProtocolVideoPopup setIsProtocolVideoShowed={setIsProtocolVideoShowed} protocolId={protocolId} />;
    }
  };

  const renderAdherenceWheels = () => {
    const plotsRef = {
      scorePlotRef,
      difficultyModulatorsPlotRef,
      hitsAndErrorsPlotRef,
      sessionDurationPlotRef,
      distanceCoveredPlotRef,
      areaCoveredPlotRef,
      leftElbowPlotRef,
      rightElbowPlotRef,
      totalDisplacementPlotRef,
      volumePlotRef,
      surfaceAreaPlotRef,
      AreaXYZPlotRef,
    };
    return (
      <AdherenceContainer
        patientId={patientId}
        protocolId={protocolId}
        software={software}
        sessionTimeRange={sessionTimeRange}
        setSessionTimeRange={setSessionTimeRange}
        sessionsData={currentRecordings}
        plotsRef={plotsRef}
      />
    );
  };

  const renderScorePlot = () => {
    const scoreRecordings = currentRecordings.filter((recording) => recording.hasOwnProperty("score"));
    if (scoreRecordings.length) return <ScorePlot software={software} recordings={currentRecordings} ref={scorePlotRef} />;
  };

  const renderDifficultyModulatorsPlot = () => {
    if (!isProtocolWithoutDifficultyModulators(protocolId) && isAppOrPlus(software) && currentDifficultyModulators.length) {
      return (
        <DifficultyModulatorsPlot
          recordings={currentRecordings}
          targetedDifficultyModulators={currentDifficultyModulators}
          protocolPrefix={getProtocolInfoPrefix(protocolId)}
          ref={difficultyModulatorsPlotRef}
        />
      );
    }
  };

  const renderHitsAndErrorsPlot = () => {
    if (!isProtocolWithoutHitsAndErrors()) {
      const hitsAndErrorsRecordings = currentRecordings.filter(
        (recording) => recording.hasOwnProperty("totalSuccess") || recording.hasOwnProperty("totalErrors")
      );
      if (hitsAndErrorsRecordings.length) return <HitsAndErrorsPlot recordings={currentRecordings} ref={hitsAndErrorsPlotRef} />;
    }
  };

  const renderSessionDurationPlot = () => {
    if (isClinicOrHome(software)) {
      // Filtering by 'score' because in RGS Clinic and RGS Home does not exist the field 'sessionDuration(seconds)'
      // Filtering by 'score' we make sure there exist at least one session to show this plot
      const scoreRecordings = currentRecordings.filter((recording) => recording.hasOwnProperty("score"));
      if (scoreRecordings.length)
        return <SessionDurationPlot software={software} recordings={currentRecordings} ref={sessionDurationPlotRef} />;
    } else {
      const sessionDurationRecordings = currentRecordings.filter((recording) => recording.hasOwnProperty("sessionDuration(seconds)"));
      if (sessionDurationRecordings.length)
        return <SessionDurationPlot software={software} recordings={currentRecordings} ref={sessionDurationPlotRef} />;
    }
  };

  const renderDistanceCoveredPlot = () => {
    const distanceCoveredRecordings = currentRecordings.filter(
      (recording) => recording.hasOwnProperty("distance_hand_left") || recording.hasOwnProperty("distance_hand_right")
    );
    if (distanceCoveredRecordings.length) return <DistanceCoveredPlot recordings={currentRecordings} ref={distanceCoveredPlotRef} />;
  };

  const renderAreaCoveredPlot = () => {
    const areaCoveredRecordings = currentRecordings.filter(
      (recording) => recording.hasOwnProperty("area_hand_left") || recording.hasOwnProperty("area_hand_right")
    );
    if (areaCoveredRecordings.length) return <AreaCoveredPlot recordings={currentRecordings} ref={areaCoveredPlotRef} />;
  };

  const renderLeftElbowPlot = () => {
    const leftElbowRecordings = currentRecordings.filter(
      (recording) => recording.hasOwnProperty("elbow_range_left_min") || recording.hasOwnProperty("elbow_range_left_max")
    );
    if (leftElbowRecordings.length) return <LeftElbowPlot recordings={currentRecordings} ref={leftElbowPlotRef} />;
  };

  const renderRightElbowPlot = () => {
    const rightElbowRecordings = currentRecordings.filter(
      (recording) => recording.hasOwnProperty("elbow_range_right_min") || recording.hasOwnProperty("elbow_range_right_max")
    );
    if (rightElbowRecordings.length) return <RightElbowPlot recordings={currentRecordings} ref={rightElbowPlotRef} />;
  };

  const renderTotalDisplacementPlot = () => {
    const totalDisplacementMetrics = currentMetrics.filter((metric) => metric.hasOwnProperty("totalDisplacement"));
    if (totalDisplacementMetrics.length) return <TotalDisplacementPlot metrics={currentMetrics} ref={totalDisplacementPlotRef} />;
  };

  const renderVolumePlot = () => {
    const volumeMetrics = currentMetrics.filter((metric) => metric.hasOwnProperty("volume"));
    if (volumeMetrics.length) return <VolumePlot metrics={currentMetrics} ref={volumePlotRef} />;
  };

  const renderSurfaceAreaPlot = () => {
    const surfaceAreaMetrics = currentMetrics.filter((metric) => metric.hasOwnProperty("surfaceArea"));
    if (surfaceAreaMetrics.length) return <SurfaceAreaPlot metrics={currentMetrics} ref={surfaceAreaPlotRef} />;
  };

  const renderAreaXYZPlot = () => {
    const AreaXYZMetrics = currentMetrics.filter((metric) => metric.hasOwnProperty("area(x,y,z)"));
    if (AreaXYZMetrics.length) return <AreaXYZPlot metrics={currentMetrics} ref={AreaXYZPlotRef} />;
  };

  const renderHeaderTable = () => {
    if (currentRecordings.length) {
      return (
        <>
          <Title title={t("sessions").toUpperCase()} marginBottom="10px" />
          <SessionHeaderMainDiv>
            <SessionHeaderDiv>
              <SessionTitle width="100px" width910="75px">
                {t("session").toUpperCase()}
              </SessionTitle>
              <SessionTitle width="138px" width910="80px">
                {t("date").toUpperCase()}
              </SessionTitle>
              <SessionTitle width="108px" width910="80px">
                {t("time").toUpperCase()}
              </SessionTitle>
              <SessionHideDiv>
                <SessionTitle width="120px">{t("score").toUpperCase()}</SessionTitle>
                {isAppOrPlus(software) && <SessionTitle width="105px">{t("duration").toUpperCase()}</SessionTitle>}
                {isAppOrPlus(software) && !isProtocolWithoutDifficultyModulators(protocolId) && isAppOrPlusProtocol(protocolId) && (
                  <SessionTitle width="90px">{t("reports").toUpperCase()}</SessionTitle>
                )}
                {!isOnlyProtocolWithScore(protocolId) && (
                  <>
                    <SessionTitle width="148px">{t("reports_sessionsList_tableDistanceText")}</SessionTitle>
                    <SessionTitle width="100px">{t("reports_sessionsList_tableAreaText")}</SessionTitle>
                  </>
                )}
              </SessionHideDiv>
            </SessionHeaderDiv>
          </SessionHeaderMainDiv>
        </>
      );
    }
  };

  const renderTotalSessions = () => {
    const sortedSessions = [...currentRecordings].sort((a, b) => {
      return new Date(b.STARTING_DATE) - new Date(a.STARTING_DATE);
    });

    return sortedSessions.map((session, i) => {
      const sessionIndex = sortedSessions.length - i;
      const sessionId = session.SESSION_ID;
      const date = moment(session.STARTING_DATE).format("DD/MM/YYYY");
      const hour = moment(session.STARTING_DATE).format("HH:mm:ss");

      const getLeftAndRightValues = (value) => {
        if (!isClinicOrHome(software)) return false;
        if (!value) return 0;
        return Number(value).toFixed(2);
      };

      const sessionDuration = session["sessionDuration(seconds)"]
        ? getTrainedMinutesAndSeconds(Number(session["sessionDuration(seconds)"]))
        : isAppOrPlus(software)
        ? getTrainedMinutesAndSeconds(0)
        : false;

      return (
        <SessionData
          key={sessionId}
          sessionId={sessionId}
          sessionNumber={sessionIndex}
          date={date}
          hour={hour}
          score={session.score ? session.score : 0}
          distanceLeft={getLeftAndRightValues(session.distance_hand_left)}
          distanceRight={getLeftAndRightValues(session.distance_hand_right)}
          areaLeft={getLeftAndRightValues(session.area_hand_left)}
          areaRight={getLeftAndRightValues(session.area_hand_right)}
          trainedTime={sessionDuration}
          software={software}
          toggleRechargeReports={toggleRechargeReports}
          setToggleRechargeReports={setToggleRechargeReports}
          setIsLoadingReports={setIsLoadingReports}
        />
      );
    });
  };

  return (
    <>
      {isLoadingReports ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          {!isAISNHospital && <ProtocolNavigation />}
          <Title title={protocolTitle} marginBottom="10px" />
          {renderButtonExtraInfo()}
          {renderExtraProtocolInfo()}
          {renderProtocolVideoPopup()}
          {!recordings.length ? (
            <IndicatorDataDiv>
              <IndicatorDataText>{t("reports_sessionsRecordedMessage")}</IndicatorDataText>
            </IndicatorDataDiv>
          ) : (
            <>
              {renderAdherenceWheels()}
              {renderScorePlot()}
              {renderDifficultyModulatorsPlot()}
              {renderHitsAndErrorsPlot()}
              {renderSessionDurationPlot()}
              {isClinicOrHome(software) && (
                <>
                  {renderDistanceCoveredPlot()}
                  {renderAreaCoveredPlot()}
                  {renderLeftElbowPlot()}
                  {renderRightElbowPlot()}
                </>
              )}
              {validateApplicationProtocol(protocols.app.ar, protocolId) && (
                <>
                  {renderTotalDisplacementPlot()}
                  {renderVolumePlot()}
                  {renderSurfaceAreaPlot()}
                  {renderAreaXYZPlot()}
                </>
              )}
              {renderHeaderTable()}
              {renderTotalSessions()}
            </>
          )}
        </>
      )}
    </>
  );
};

export default Reports;
